<template>
    <div class="dock-weather-icon" :class="loading ? 'dock-weather--sun' : noLocation ? 'dock-weather--' + showcaseEffect : conditionClass">
        <div class="dock-weather-bg"></div>
        <template v-if="loading">
            <div v-if="dockAnimation" class="dock-weather-effects">
                <span class="dock-weather-sun"></span>
            </div>
            <div class="dock-weather-info">
                <span class="dock-weather-loading-spinner"></span>
            </div>
        </template>
        <template v-else-if="noLocation">
            <transition name="dock-weather-showcase" mode="out-in">
                <div class="dock-weather-effects" :key="showcaseEffect">
                    <template v-if="showcaseEffect === 'sun'">
                        <span class="dock-weather-sun"></span>
                    </template>
                    <template v-else-if="showcaseEffect === 'rain'">
                        <span v-for="i in 14" :key="'r'+i" class="dock-weather-drop" :style="dropStyle(i)"></span>
                    </template>
                    <template v-else-if="showcaseEffect === 'thunder'">
                        <span v-for="i in 14" :key="'r'+i" class="dock-weather-drop" :style="dropStyle(i)"></span>
                        <span class="dock-weather-flash"></span>
                        <span class="dock-weather-bolt"></span>
                    </template>
                    <template v-else-if="showcaseEffect === 'snow'">
                        <span v-for="i in 11" :key="'s'+i" class="dock-weather-flake" :style="flakeStyle(i)"></span>
                    </template>
                </div>
            </transition>
            <div class="dock-weather-info">
                <span class="dock-weather-showcase-title">{{ $t('common.weather_word') }}<br>{{ $t('common.condition_word') }}</span>
            </div>
        </template>
        <template v-else>
            <div v-if="dockAnimation" class="dock-weather-effects">
                <template v-if="effectType === 'rain' || effectType === 'thunder'">
                    <span v-for="i in 14" :key="'r'+i" class="dock-weather-drop" :style="dropStyle(i)"></span>
                    <template v-if="effectType === 'thunder'">
                        <span class="dock-weather-flash"></span>
                        <span class="dock-weather-bolt"></span>
                    </template>
                    <span class="dock-weather-puddle"></span>
                </template>
                <template v-else-if="effectType === 'snow'">
                    <span v-for="i in 11" :key="'s'+i" class="dock-weather-flake" :style="flakeStyle(i)"></span>
                </template>
                <template v-else-if="effectType === 'cloud'">
                    <span class="dock-weather-cloud dock-weather-cloud--1"></span>
                    <span class="dock-weather-cloud dock-weather-cloud--2"></span>
                </template>
                <template v-else-if="effectType === 'sun'">
                    <span class="dock-weather-sun"></span>
                </template>
                <template v-else-if="effectType === 'partcloud'">
                    <span class="dock-weather-sun dock-weather-sun--small"></span>
                    <span class="dock-weather-cloud dock-weather-cloud--front"></span>
                </template>
                <template v-else-if="effectType === 'partcloud_night'">
                    <span class="dock-weather-moon"></span>
                    <span class="dock-weather-cloud dock-weather-cloud--front"></span>
                </template>
                <template v-else-if="effectType === 'fog'">
                    <span class="dock-weather-fog dock-weather-fog--1"></span>
                    <span class="dock-weather-fog dock-weather-fog--2"></span>
                    <span class="dock-weather-fog dock-weather-fog--3"></span>
                </template>
                <template v-else-if="effectType === 'night'">
                    <span class="dock-weather-moon"></span>
                    <span class="dock-weather-star dock-weather-star--1"></span>
                    <span class="dock-weather-star dock-weather-star--2"></span>
                    <span class="dock-weather-star dock-weather-star--3"></span>
                    <span class="dock-weather-star dock-weather-star--4"></span>
                </template>
                <template v-else>
                    <span class="dock-weather-sun dock-weather-sun--small"></span>
                    <span class="dock-weather-cloud dock-weather-cloud--front"></span>
                </template>
            </div>
            <div class="dock-weather-info">
                <span class="dock-weather-hero-temp">{{ temperature }}°</span>
                <template v-if="dockAnimation">
                    <transition name="dock-weather-cycle" mode="out-in" @after-enter="checkCycleOverflow">
                        <span class="dock-weather-cycle-wrap" :key="cycleIndex">
                            <span class="dock-weather-cycle-text" ref="cycleText" v-html="cycleText"></span>
                        </span>
                    </transition>
                </template>
                <span v-else class="dock-weather-cycle-wrap">
                    <span class="dock-weather-cycle-text">{{ cityName }}</span>
                </span>
            </div>
        </template>
    </div>
</template>

<script>
import weatherService from './weatherService'

const ICON_EFFECT = {
    thunderstorm: 'thunder',
    rain: 'rain', rain_shower: 'rain', freezing_rain: 'rain',
    drizzle: 'rain', freezing_drizzle: 'rain',
    snow: 'snow', snow_grains: 'snow', snow_shower: 'snow',
    fog: 'fog',
    clear: 'sun', mainly_clear: 'sun',
    night_clear: 'night',
    partly_cloudy: 'partcloud',
    overcast: 'cloud',
}

export default {
    name: 'DockWeatherIcon',
    data() {
        return {
            cycleIndex: 0,
            showcaseIndex: 0,
        }
    },
    computed: {
        dockAnimation() {
            return weatherService.state.dockAnimation
        },
        noLocation() {
            return !this.loc
        },
        loading() {
            return this.loc != null && !this.loc.data
        },
        showcaseEffect() {
            return ['sun', 'rain', 'thunder', 'snow'][this.showcaseIndex]
        },
        loc() {
            return weatherService.defaultLocation.value
        },
        current() {
            return this.loc?.data?.current || null
        },
        daily() {
            return this.loc?.data?.daily || []
        },
        temperature() {
            return this.current?.temperature != null ? Math.round(this.current.temperature) : '--'
        },
        conditionLabel() {
            return this.current?.condition?.label || ''
        },
        todayMinMax() {
            const today = this.daily[0]
            if (!today || today.temp_min == null || today.temp_max == null) return ''
            return Math.round(today.temp_min) + '°\u00A0<span style="display:inline;opacity:0.4">·</span>\u00A0' + Math.round(today.temp_max) + '°'
        },
        cityName() {
            if (!this.loc) return ''
            return this.loc.cityName || this.loc.districtName || ''
        },
        cycleText() {
            const texts = [this.cityName, this.conditionLabel, this.todayMinMax]
            return texts[this.cycleIndex] || this.cityName
        },
        isNight() {
            if (!this.loc?.data) return false
            const now = new Date()
            const today = this.daily[0]
            if (!today?.sunrise || !today?.sunset) return false
            const sunrise = this.parseTime(today.sunrise)
            const sunset = this.parseTime(today.sunset)
            return now < sunrise || now > sunset
        },
        effectType() {
            if (!this.current) return this.isNight ? 'partcloud_night' : 'partcloud'
            const icon = this.current.condition?.icon || this.current.condition_icon
            if (icon) {
                if (['clear', 'mainly_clear'].includes(icon)) return this.isNight ? 'night' : 'sun'
                if (icon === 'night_clear') return 'night'
                const mapped = ICON_EFFECT[icon] || 'partcloud'
                if (mapped === 'partcloud') return this.isNight ? 'partcloud_night' : 'partcloud'
                return mapped
            }
            const id = Number(this.current.condition?.id || this.current.condition_id) || 0
            if (id) {
                if ([95, 96, 99].includes(id)) return 'thunder'
                if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(id)) return 'rain'
                if ([71, 73, 75, 77, 85, 86].includes(id)) return 'snow'
                if ([45, 48].includes(id)) return 'fog'
                if ([1, 2].includes(id)) return this.isNight ? 'partcloud_night' : 'partcloud'
                if ([3].includes(id)) return 'cloud'
                return this.isNight ? 'night' : 'sun'
            }
            const label = (this.current.condition?.label || '').toLowerCase()
            if (label.includes('gök gürültü') || label.includes('thunder')) return 'thunder'
            if (label.includes('yağmur') || label.includes('rain') || label.includes('çisenti')) return 'rain'
            if (label.includes('kar') || label.includes('snow')) return 'snow'
            if (label.includes('sis') || label.includes('fog')) return 'fog'
            if (label.includes('açık') || label.includes('clear')) return this.isNight ? 'night' : 'sun'
            if (label.includes('bulut') || label.includes('cloud') || label.includes('kapalı')) return 'cloud'
            return this.isNight ? 'partcloud_night' : 'partcloud'
        },
        conditionClass() {
            return 'dock-weather--' + this.effectType
        },
    },
    watch: {
        dockAnimation(val) {
            if (val) {
                this._startCycleTimer()
            } else {
                clearInterval(this._cycleTimer)
                this.cycleIndex = 0
            }
        },
        noLocation: {
            immediate: true,
            handler(val) {
                if (val) {
                    this._startShowcaseTimer()
                } else {
                    clearInterval(this._showcaseTimer)
                    this.showcaseIndex = 0
                }
            },
        },
    },
    mounted() {
        weatherService.init()
        if (this.dockAnimation) this._startCycleTimer()
    },
    beforeUnmount() {
        clearInterval(this._cycleTimer)
        clearInterval(this._showcaseTimer)
    },
    methods: {
        _startCycleTimer() {
            clearInterval(this._cycleTimer)
            this._cycleTimer = setInterval(() => {
                this.cycleIndex = (this.cycleIndex + 1) % 3
            }, 3000)
        },
        _startShowcaseTimer() {
            clearInterval(this._showcaseTimer)
            this._showcaseTimer = setInterval(() => {
                this.showcaseIndex = (this.showcaseIndex + 1) % 4
            }, 4000)
        },
        dropStyle(i) {
            const left = ((i * 7.14 + (i * 3) % 7) % 95)
            const delay = -((i * 0.05) % 0.7)
            const dur = 0.5 + (i % 5) * 0.08
            return {
                left: left + '%',
                animationDelay: delay + 's',
                animationDuration: dur + 's',
            }
        },
        flakeStyle(i) {
            const left = ((i * 9.1 + (i * 5) % 11) % 100)
            const delay = -((i * 0.25) % 2.5)
            const dur = 2 + (i % 3) * 0.5
            return {
                left: left + '%',
                animationDelay: delay + 's',
                animationDuration: dur + 's',
            }
        },
        checkCycleOverflow() {
            const el = this.$refs.cycleText
            if (!el) return
            el.classList.remove('dock-weather-cycle-text--scroll')
            void el.offsetWidth
            const parent = el.parentElement
            if (el.scrollWidth > parent.clientWidth + 1) {
                el.style.setProperty('--scroll-dist', (parent.clientWidth - el.scrollWidth) + 'px')
                requestAnimationFrame(() => {
                    el.classList.add('dock-weather-cycle-text--scroll')
                })
            }
        },
        parseTime(str) {
            if (!str) return new Date()
            const d = new Date()
            const parts = str.split(':')
            d.setHours(parseInt(parts[0]) || 0, parseInt(parts[1]) || 0, 0, 0)
            return d
        },
    },
}
</script>

<style scoped>
.dock-weather-icon span {
    display: block;
}

.dock-weather-icon {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 22.5%;
    overflow: hidden;
}

.dock-weather-loading-spinner {
    width: 34%;
    height: 34%;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-top-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    animation: dockWeatherSpin 0.8s linear infinite;
}

@keyframes dockWeatherSpin {
    to { transform: rotate(360deg); }
}

.dock-weather-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    transition: background 0.5s ease;
}

.dock-weather--sun .dock-weather-bg {
    background: linear-gradient(170deg, #1B3A5C 0%, #142D4A 40%, #0E203A 100%);
}
.dock-weather--night .dock-weather-bg {
    background: linear-gradient(170deg, #121840 0%, #0D1233 50%, #090E28 100%);
}
.dock-weather--partcloud .dock-weather-bg {
    background: linear-gradient(170deg, #1E2D42 0%, #162336 50%, #10192B 100%);
}
.dock-weather--partcloud_night .dock-weather-bg {
    background: linear-gradient(170deg, #121840 0%, #0D1233 50%, #090E28 100%);
}
.dock-weather--cloud .dock-weather-bg {
    background: linear-gradient(170deg, #1C242E 0%, #151C25 50%, #10161E 100%);
}
.dock-weather--rain .dock-weather-bg {
    background: linear-gradient(170deg, #213654 0%, #182A3E 50%, #0F1E2E 100%);
}
.dock-weather--snow .dock-weather-bg {
    background: linear-gradient(170deg, #223050 0%, #1A2540 50%, #121C30 100%);
}
.dock-weather--thunder .dock-weather-bg {
    background: linear-gradient(170deg, #1E1530 0%, #160F26 50%, #100A1E 100%);
}
.dock-weather--fog .dock-weather-bg {
    background: linear-gradient(170deg, #1C2228 0%, #161B21 50%, #11161B 100%);
}

.dock-weather-effects {
    position: absolute;
    inset: 0;
    z-index: 1;
    overflow: hidden;
    pointer-events: none;
}

.dock-weather-sun {
    position: absolute;
    width: 40%;
    height: 40%;
    top: 6%;
    right: 6%;
    border-radius: 50%;
    background: radial-gradient(circle, #FFE082 10%, #FFB74D 50%, rgba(255, 183, 77, 0) 100%);
    box-shadow: 0 0 14px 8px rgba(255, 183, 77, 0.6);
    animation: dockSunPulse 4s ease-in-out infinite;
}
.dock-weather-sun--small {
    width: 30%;
    height: 30%;
    top: 5%;
    right: 5%;
    box-shadow: 0 0 10px 4px rgba(255, 183, 77, 0.5);
}

@keyframes dockSunPulse {
    0%, 100% { box-shadow: 0 0 14px 8px rgba(255, 183, 77, 0.6); transform: scale(1); }
    50% { box-shadow: 0 0 20px 14px rgba(255, 183, 77, 0.8); transform: scale(1.08); }
}

.dock-weather-moon {
    position: absolute;
    width: 28%;
    height: 28%;
    bottom: 12%;
    right: 8%;
    border-radius: 50%;
    background: transparent;
    box-shadow: inset -5px 1px 0 0 #F5F0E0;
    filter: drop-shadow(0 0 4px rgba(245, 240, 224, 0.3));
    transform: rotate(30deg);
}

.dock-weather-moon--small {
    width: 22%;
    height: 22%;
    bottom: 14%;
    right: 6%;
    box-shadow: inset -4px 1px 0 0 #F5F0E0;
    filter: drop-shadow(0 0 3px rgba(245, 240, 224, 0.25));
    transform: rotate(30deg);
}

.dock-weather-star {
    position: absolute;
    width: 4px;
    height: 4px;
    background: #E8E0F0;
    border-radius: 50%;
    box-shadow: 0 0 5px rgba(200, 200, 255, 0.5);
    animation: dockStarTwinkle 2s ease-in-out infinite;
}
.dock-weather-star--1 { top: 14%; left: 16%; }
.dock-weather-star--2 { top: 30%; left: 52%; animation-delay: 0.7s; width: 3px; height: 3px; }
.dock-weather-star--3 { top: 10%; right: 40%; animation-delay: 1.3s; width: 3px; height: 3px; }
.dock-weather-star--4 { top: 44%; left: 24%; animation-delay: 1.8s; width: 3px; height: 3px; }

@keyframes dockStarTwinkle {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.3); }
}

.dock-weather-cloud {
    position: absolute;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 50px;
    height: 20%;
    filter: blur(2px);
}
.dock-weather-cloud::before,
.dock-weather-cloud::after {
    content: '';
    position: absolute;
    background: inherit;
    border-radius: 50%;
}
.dock-weather-cloud::before {
    width: 40%;
    height: 130%;
    top: -50%;
    left: 22%;
}
.dock-weather-cloud::after {
    width: 30%;
    height: 110%;
    top: -35%;
    left: 52%;
}
.dock-weather-cloud--1 {
    width: 58%;
    top: 22%;
    left: 2%;
    animation: dockCloudDrift1 10s ease-in-out infinite;
}
.dock-weather-cloud--2 {
    width: 52%;
    top: 58%;
    left: 42%;
    opacity: 0.7;
    animation: dockCloudDrift2 12s ease-in-out infinite;
}
.dock-weather-cloud--front {
    width: 70%;
    height: 24%;
    top: 26%;
    left: 8%;
    filter: blur(2px);
    background: rgba(255, 255, 255, 0.5);
    animation: dockCloudDrift1 7s ease-in-out infinite;
}
.dock-weather-cloud--front::before,
.dock-weather-cloud--front::after {
    content: '';
    position: absolute;
    background: inherit;
    border-radius: 50%;
}
.dock-weather-cloud--front::before {
    width: 45%;
    height: 140%;
    top: -60%;
    left: 18%;
}
.dock-weather-cloud--front::after {
    width: 35%;
    height: 120%;
    top: -40%;
    left: 50%;
}

@keyframes dockCloudDrift1 {
    0%, 100% { transform: translateX(0); }
    50% { transform: translateX(12%); }
}
@keyframes dockCloudDrift2 {
    0%, 100% { transform: translateX(0); }
    50% { transform: translateX(-10%); }
}

.dock-weather-drop {
    position: absolute;
    top: 0;
    width: 1.5px;
    height: 10%;
    background: rgba(140, 210, 255, 0.85);
    border-radius: 1px;
    animation: dockRainFall 0.7s linear infinite;
}

@keyframes dockRainFall {
    0% { transform: translateY(0) translateX(0); opacity: 0.9; }
    100% { transform: translateY(900%) translateX(-2px); opacity: 0.2; }
}

.dock-weather-flake {
    position: absolute;
    top: 5%;
    width: 3.5px;
    height: 3.5px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    box-shadow: 0 0 4px rgba(255, 255, 255, 0.4);
    animation: dockSnowFall 2.5s linear infinite;
}

@keyframes dockSnowFall {
    0% { transform: translateY(0) translateX(0); opacity: 0.9; }
    100% { transform: translateY(1200%) translateX(4px); opacity: 0.1; }
}

.dock-weather-flash {
    position: absolute;
    inset: 0;
    background: rgba(200, 180, 255, 0.18);
    opacity: 0;
    animation: dockFlashPulse 2.5s ease-in-out infinite;
}

@keyframes dockFlashPulse {
    0%, 74%, 100% { opacity: 0; }
    76% { opacity: 1; }
    78% { opacity: 0.1; }
    80% { opacity: 0.9; }
    84% { opacity: 0; }
}

.dock-weather-bolt {
    position: absolute;
    top: 8%;
    left: 32%;
    width: 30%;
    height: 50%;
    background: none;
    opacity: 0;
    animation: dockBoltFlash 2.5s ease-in-out infinite;
    filter: drop-shadow(0 0 6px rgba(255, 230, 109, 0.8));
}
.dock-weather-bolt::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, #FFE66D, #FFF);
    clip-path: polygon(50% 0%, 30% 45%, 55% 45%, 35% 100%, 70% 38%, 45% 38%);
}

@keyframes dockBoltFlash {
    0%, 74%, 100% { opacity: 0; }
    76% { opacity: 1; }
    78% { opacity: 0.1; }
    80% { opacity: 0.95; }
    84% { opacity: 0; }
}

.dock-weather-fog {
    position: absolute;
    left: -10%;
    width: 120%;
    height: 14%;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 20px;
    filter: blur(6px);
}
.dock-weather-fog--1 {
    top: 18%;
    opacity: 0.75;
    animation: dockFogDrift 6s ease-in-out infinite;
}
.dock-weather-fog--2 {
    top: 36%;
    opacity: 0.55;
    animation: dockFogDrift 8s ease-in-out infinite reverse;
}
.dock-weather-fog--3 {
    top: 52%;
    opacity: 0.45;
    animation: dockFogDrift 7s ease-in-out infinite 1s;
}

@keyframes dockFogDrift {
    0%, 100% { transform: translateX(-5%) scaleY(1); opacity: 0.45; }
    50% { transform: translateX(5%) scaleY(1.15); opacity: 0.75; }
}

.dock-weather-info {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
    pointer-events: none;
}

.dock-weather-hero-temp {
    font-size: calc(var(--dock-icon-size, 48px) * 0.38);
    font-weight: 700;
    color: #fff;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
    letter-spacing: -0.02em;
    line-height: 1.1;
}

.dock-weather-cycle-wrap {
    max-width: 82%;
    clip-path: inset(-4px 0);
    text-align: center;
    margin-top: 1px;
}

.dock-weather-cycle-text {
    display: inline-block;
    white-space: nowrap;
    font-size: calc(var(--dock-icon-size, 48px) * 0.16);
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
    line-height: 1.3;
}

.dock-weather-cycle-text--scroll {
    animation: dockCycleScroll 2.2s ease-in-out;
}

@keyframes dockCycleScroll {
    0%, 12% { transform: translateX(0); }
    45%, 55% { transform: translateX(var(--scroll-dist)); }
    88%, 100% { transform: translateX(0); }
}

.dock-weather-cycle-enter-active,
.dock-weather-cycle-leave-active {
    transition: opacity 0.35s ease, transform 0.35s ease;
}
.dock-weather-cycle-enter-from {
    opacity: 0;
    transform: translateY(20%);
}
.dock-weather-cycle-leave-to {
    opacity: 0;
    transform: translateY(-20%);
}

.dock-weather-showcase-title {
    font-size: calc(var(--dock-icon-size, 48px) * 0.18);
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
    line-height: 1.15;
    text-align: center;
}

.dock-weather-showcase-enter-active,
.dock-weather-showcase-leave-active {
    transition: opacity 0.6s ease;
}
.dock-weather-showcase-enter-from,
.dock-weather-showcase-leave-to {
    opacity: 0;
}
</style>
